/**
 * tokens/cssGenerator.js - Generate scoped CSS variables from tokens
 * Location: /app/frontend/src/admin/page_builder/tokens/
 * 
 * Generates CSS variables scoped under .tc-token-scope class
 * to avoid polluting global styles.
 */

import { defaultTokens, tokenPathToCSSVar } from './config';

/**
 * CSS scope class name - all token vars are scoped under this
 */
export const TOKEN_SCOPE_CLASS = 'tc-token-scope';

/**
 * Recursively flatten nested token object into flat CSS variable entries
 * @param {object} obj - Nested token object
 * @param {string} prefix - Current path prefix
 * @returns {Array<{path: string, varName: string, value: string}>}
 */
function flattenTokens(obj, prefix = '') {
  const entries = [];
  
  for (const [key, value] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${key}` : key;
    
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      // Recurse into nested objects
      entries.push(...flattenTokens(value, path));
    } else {
      // Leaf value - create CSS var entry
      entries.push({
        path,
        varName: tokenPathToCSSVar(path),
        value: String(value)
      });
    }
  }
  
  return entries;
}

/**
 * Generate CSS text with all token variables scoped under .tc-token-scope
 * @param {object} tokens - Token object (defaults to defaultTokens)
 * @returns {string} CSS text
 */
export function generateScopedCSS(tokens = defaultTokens) {
  const entries = flattenTokens(tokens);
  
  if (entries.length === 0) {
    return '';
  }
  
  const declarations = entries
    .map(({ varName, value }) => `  ${varName}: ${value};`)
    .join('\n');
  
  return `.${TOKEN_SCOPE_CLASS} {\n${declarations}\n}`;
}

/**
 * Generate a Map of CSS variable name -> value for quick lookup
 * @param {object} tokens - Token object
 * @returns {Map<string, string>} Map of varName -> value
 */
export function generateTokenMap(tokens = defaultTokens) {
  const entries = flattenTokens(tokens);
  const map = new Map();
  
  for (const { varName, value } of entries) {
    map.set(varName, value);
  }
  
  return map;
}

/**
 * Generate a Map of token path -> value for quick lookup
 * @param {object} tokens - Token object
 * @returns {Map<string, string>} Map of path -> value
 */
export function generatePathMap(tokens = defaultTokens) {
  const entries = flattenTokens(tokens);
  const map = new Map();
  
  for (const { path, value } of entries) {
    map.set(path, value);
  }
  
  return map;
}

/**
 * Get all flattened token entries
 * @param {object} tokens - Token object
 * @returns {Array<{path: string, varName: string, value: string}>}
 */
export function getTokenEntries(tokens = defaultTokens) {
  return flattenTokens(tokens);
}

export default {
  TOKEN_SCOPE_CLASS,
  generateScopedCSS,
  generateTokenMap,
  generatePathMap,
  getTokenEntries
};
